import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { Subscription } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { ShipmentDashboardBoardItem, ShipmentDashboardOverview } from './shipment-dashboard.model';
import { ShipmentDashboardService } from './shipment-dashboard.service';

interface ShipmentDashboardFilters {
  fromDate: Date | null;
  toDate: Date | null;
  shipmentNumber: string;
  deliveryNumber: string;
  customer: string;
  shipmentStatus: string | null;
  deliveryStatus: string | null;
  workflowState: string;
  routeType: string | null;
  serviceCategory: string | null;
  source: string;
  destination: string;
  readinessStatus: string | null;
  financeStatus: string | null;
  exceptionSeverity: string | null;
  pricingRateSource: string | null;
}

@Component({
  selector: 'app-shipment-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    ChartModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    DashboardCompanyScopeSelectorComponent,
    PageDataPanelComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageShellComponent
  ],
  templateUrl: './shipment-dashboard.component.html',
  styleUrls: ['./shipment-dashboard.component.css']
})
export class ShipmentDashboardComponent implements OnInit, OnDestroy {
  canViewDashboard = false;
  canViewShipmentPricing = false;
  isLoading = false;
  companyId: number | null = null;
  companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;
  private readonly subscriptions = new Subscription();

  breadcrumbItems: MenuItem[] = [
    { label: 'Shipments' },
    { label: 'Dashboard' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  filters: ShipmentDashboardFilters = this.defaultFilters();
  overview: ShipmentDashboardOverview = {};
  statusChartData: any;
  deliveryChartData: any;
  workflowChartData: any;
  routeChartData: any;
  marginChartData: any;
  chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } }
    }
  };

  shipmentStatusOptions = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Fulfilled', value: 'FULFILLED' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Posted to Accounts', value: 'POSTED_TO_ACCOUNTS' },
    { label: 'Invoiced', value: 'INVOICED' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];
  readinessOptions = [
    { label: 'Ready', value: 'READY' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Overdue', value: 'OVERDUE' }
  ];
  financeOptions = [
    { label: 'Ready', value: 'READY' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Open', value: 'OPEN' },
    { label: 'Blocked', value: 'BLOCKED' }
  ];
  severityOptions = [
    { label: 'Danger', value: 'danger' },
    { label: 'Warning', value: 'warn' },
    { label: 'Info', value: 'info' }
  ];
  routeTypeOptions = [
    { label: 'Domestic', value: 'DOMESTIC' },
    { label: 'Cross Border', value: 'CROSS_BORDER' },
    { label: 'International', value: 'INTERNATIONAL' }
  ];
  serviceCategoryOptions = [
    { label: 'Full Service', value: 'FULL_SERVICE' },
    { label: 'Transport Only', value: 'TRANSPORT_ONLY' },
    { label: 'Customs Only', value: 'CUSTOMS_ONLY' },
    { label: 'Storage', value: 'STORAGE' }
  ];
  pricingRateSourceOptions = [
    { label: 'Contract', value: 'CONTRACT' },
    { label: 'Rate Card', value: 'RATE_CARD' },
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Fallback', value: 'FALLBACK' }
  ];

  constructor(
    private dashboardService: ShipmentDashboardService,
    private permissionService: PermissionService,
    private tenantScopeService: TenantScopeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.canViewDashboard =
      this.permissionService.hasPermission(app_permissions.shipments.dashboard.view, 'ANY') ||
      this.permissionService.hasPermission(app_permissions.shipments.view);
    this.canViewShipmentPricing = this.permissionService.hasPermission(app_permissions.shipments.pricing.view, 'ANY');
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get metrics(): PageMetric[] {
    const summary = this.overview.summary || {};
    const metrics: PageMetric[] = [
      { label: 'Open Shipments', value: summary.openShipments || 0, tone: 'info' },
      { label: 'Active Deliveries', value: summary.activeDeliveries || 0, tone: 'info' },
      { label: 'Delayed Pickup', value: summary.delayedPickup || 0, tone: 'danger' },
      { label: 'Delayed Delivery', value: summary.delayedDelivery || 0, tone: 'danger' },
      { label: 'Workflow Blocked', value: summary.workflowBlocked || 0, tone: 'danger' },
      { label: 'POD Pending', value: summary.podPending || 0, tone: 'warn' }
    ];
    if (this.canViewShipmentPricing) {
      metrics.push(
        { label: 'Gross Margin', value: this.money(summary.grossMargin), tone: (summary.grossMargin || 0) < 0 ? 'danger' : 'success' },
        { label: 'Invoice-ready', value: this.money(summary.invoiceReadyAmount), tone: 'warn' },
        { label: 'Open AR', value: this.money(summary.openArAmount), tone: 'danger' }
      );
    }
    return metrics;
  }

  get activeFilterCount(): number {
    return Object.values(this.filters).filter(value => value !== null && value !== '').length;
  }

  get showGroupCompanySelector(): boolean {
    return !!this.companyGroupId && this.groupCompanies.length > 0;
  }

  loadDashboard(): void {
    if (!this.companyId && !this.companyGroupId) return;
    this.isLoading = true;
    this.dashboardService.getOverview(this.buildParams())
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: overview => {
          this.overview = overview || {};
          this.buildCharts();
        },
        error: () => {
          this.overview = {};
          this.buildCharts();
        }
      });
  }

  clearFilters(): void {
    this.filters = this.defaultFilters();
    this.loadDashboard();
  }

  openShipment(shipmentId?: number): void {
    if (shipmentId) this.router.navigate(['/shipment/view', shipmentId]);
  }

  openDelivery(deliveryId?: number): void {
    if (deliveryId) this.router.navigate(['/shipment/delivery/view', deliveryId]);
  }

  openInvoice(invoiceId?: number): void {
    if (invoiceId) this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', invoiceId]);
  }

  openBill(billId?: number): void {
    if (billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', billId]);
  }

  onDashboardCompanyScopeChange(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.loadDashboard();
  }

  tagSeverity(value?: string): string {
    switch ((value || '').toLowerCase()) {
      case 'danger':
      case 'blocked':
      case 'overdue':
      case 'cancelled':
        return 'danger';
      case 'warn':
      case 'warning':
      case 'pending':
      case 'draft':
      case 'open':
      case 'ready':
        return 'warn';
      case 'success':
      case 'completed':
      case 'closed':
      case 'invoiced':
        return 'success';
      case 'info':
      case 'in_progress':
        return 'info';
      default:
        return 'secondary';
    }
  }

  money(value?: number, currency = 'SAR'): string {
    return `${(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }

  private buildParams(): HttpParams {
    let params = new HttpParams();
    if (this.companyGroupId) {
      params = params.set('companyGroupId', String(this.companyGroupId));
      if (this.selectedGroupCompanyId) {
        params = params.set('memberCompanyId', String(this.selectedGroupCompanyId));
      }
    } else if (this.companyId) {
      params = params.set('companyId', String(this.companyId));
    }
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value === null || value === '') return;
      params = params.set(key, value instanceof Date ? this.formatDate(value) : String(value));
    });
    return params;
  }

  private buildCharts(): void {
    this.statusChartData = this.toChartData(this.overview.statusBreakdown || [], ['#0ea5e9', '#f59e0b', '#10b981', '#64748b', '#ef4444']);
    this.deliveryChartData = this.toChartData(this.overview.deliveryStatusBreakdown || [], ['#14b8a6', '#38bdf8', '#f59e0b', '#ef4444', '#64748b']);
    this.workflowChartData = this.toChartData(this.overview.workflowAgingBuckets || [], ['#10b981', '#0ea5e9', '#f59e0b', '#ef4444']);
    this.routeChartData = this.toChartData([...(this.overview.routeTypeBreakdown || []), ...(this.overview.serviceCategoryBreakdown || [])], ['#0f766e', '#2563eb', '#f97316', '#9333ea', '#64748b']);
    this.marginChartData = this.toChartData(this.overview.marginBuckets || [], ['#ef4444', '#f97316', '#f59e0b', '#10b981']);
  }

  private toChartData(items: ShipmentDashboardBoardItem[], colors: string[]) {
    return {
      labels: items.map(item => item.label || item.key || '-'),
      datasets: [{ data: items.map(item => item.count || 0), backgroundColor: colors }]
    };
  }

  private defaultFilters(): ShipmentDashboardFilters {
    return {
      fromDate: null,
      toDate: null,
      shipmentNumber: '',
      deliveryNumber: '',
      customer: '',
      shipmentStatus: null,
      deliveryStatus: null,
      workflowState: '',
      routeType: null,
      serviceCategory: null,
      source: '',
      destination: '',
      readinessStatus: null,
      financeStatus: null,
      exceptionSeverity: null,
      pricingRateSource: null
    };
  }

  private formatDate(value: Date): string {
    return value.toISOString().slice(0, 10);
  }

  private handleScopeChange(scope: TenantScope | null): void {
    const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
    const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
    this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
    this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
    if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
      this.selectedGroupCompanyId = null;
    }
    if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
      return;
    }

    this.companyId = nextCompanyId;
    this.companyGroupId = nextCompanyGroupId;
    this.selectedGroupCompanyId = null;
    if (!this.canViewDashboard || (!this.companyId && !this.companyGroupId)) {
      this.overview = {};
      this.buildCharts();
      return;
    }
    this.loadDashboard();
  }
}
